import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { uid } from '@/lib/helpers';
import { Plus, Trash2, Loader2, Table2, ClipboardPaste, Eraser, CheckCircle2, AlertTriangle, XCircle, Save, FolderOpen, Clock, FileText, Pencil, Upload, CopyPlus, Wand2, Search, Copy } from 'lucide-react';

// Suffix alphabets. SAFE drops look-alike letters I and O (so codes never mix up with 1/0).
const FULL_ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const SAFE_ALPHA = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
// bijective numbering over a given alphabet: 1->A, 2->B ... (base = alphabet length)
const numToAlpha = (n, A = FULL_ALPHA) => { const b = A.length; let s = ''; while (n > 0) { const r = (n - 1) % b; s = A[r] + s; n = Math.floor((n - 1) / b); } return s; };
const alphaToNum = (a, A = FULL_ALPHA) => { const b = A.length; let n = 0; for (const ch of a.toUpperCase()) { const idx = A.indexOf(ch); if (idx < 0) return NaN; n = n * b + (idx + 1); } return n; };
const baseOf = (code) => { const m = String(code || '').match(/^(.*?)([A-Za-z]+)$/); return (m && m[2]) ? m[1] : String(code || ''); };
// If the code ends with letters (e.g. RM1102A), start from there and increment: A,B,..,Z,AA,AB.
// If it ends with a digit (e.g. AB25), append A,B,C...
const expandCodes = (code, qty, A = FULL_ALPHA) => {
  if (qty <= 1) return [code];
  const m = code.match(/^(.*?)([A-Za-z]+)$/);
  if (m && m[2]) {
    const base = m[1]; let start = alphaToNum(m[2], A);
    if (isNaN(start) || start < 1) start = 1;
    return Array.from({ length: qty }, (_, i) => `${base}${numToAlpha(start + i, A)}`);
  }
  return Array.from({ length: qty }, (_, i) => `${code}${numToAlpha(i + 1, A)}`);
};

const blankRow = (store, category = '') => ({ key: uid(), sr: '', name: '', category, code: '', price: '', cost: '', qty: '1', store_id: store });
const START_ROWS = 1;
const FIELD_ORDER = ['name', 'category', 'code', 'price', 'cost', 'qty'];

// Popup size presets — user can shrink/grow the window from the header corner.
const SIZE = {
  sm: { w: 'max-w-2xl', h: 'max-h-[38vh]', label: '50%' },
  md: { w: 'max-w-5xl', h: 'max-h-[46vh]', label: '100%' },
  lg: { w: 'max-w-[98vw]', h: 'max-h-[72vh]', label: 'Max' },
};

const RowCopy = ({ value, testid }) => (value ? (
  <button type="button" data-testid={testid} title="Copy"
    onClick={() => { try { navigator.clipboard.writeText(String(value)); } catch (e) { /* ignore */ } }}
    className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 hover:text-foreground focus-visible:opacity-100">
    <Copy className="h-3.5 w-3.5" />
  </button>
) : null);

// Memoized row: because setCell preserves object identity for unchanged rows,
// only the edited row re-renders on a keystroke (not the whole grid).
const BulkRow = React.memo(function BulkRow({ row: r, idx, stKind, stMsg, isSelected, isHighlighted, groupBg, groupBorder, groupStart, accent, filled, stores, cats, H }) {
  const h = H.current;
  const cell = (field, extra = '') => (
    <div className="group/cell relative">
      <Input value={r[field]} onChange={(e) => h.setCell(r.key, field, e.target.value)}
        onFocus={() => h.setOrigin(idx, field)} data-testid={`bulkadd-${field}-${idx}`}
        className={`h-9 rounded-md border-transparent pr-6 focus:border-input ${extra}`} />
      <RowCopy value={r[field]} testid={`bulkadd-copy-${field}-${idx}`} />
    </div>
  );
  return (
    <tr ref={(el) => h.setRowRef(r.key, el)}
      className={`border-l-4 ${groupBorder} ${groupStart ? 'border-t-2 border-t-foreground/25' : ''} [&>td]:border-b [&>td]:border-border [&>td]:p-1 ${
        isHighlighted ? 'ring-2 ring-inset ring-primary bg-primary/10' : isSelected ? 'bg-primary/5' : (accent || groupBg)}`}
      data-testid={`bulkadd-row-${idx}`}>
      <td className="px-2 text-center"><Checkbox checked={isSelected} disabled={!filled} onCheckedChange={() => h.toggleSel(r.key)} data-testid={`bulkadd-select-${idx}`} /></td>
      <td className="px-2 text-center text-xs text-muted-foreground">{idx + 1}</td>
      <td>{cell('sr')}</td>
      <td>{cell('name')}</td>
      <td>
        <div className="group/cell relative">
          <Input list="bulkadd-cats" value={r.category} onChange={(e) => { const v = e.target.value; h.setCell(r.key, 'category', v); if (v && cats.some((c) => (c.name || '').toLowerCase() === v.trim().toLowerCase())) h.suggestCodeForCategory(r.key, v); }}
            onFocus={() => h.setOrigin(idx, 'category')} onBlur={(e) => h.suggestCodeForCategory(r.key, e.target.value)}
            data-testid={`bulkadd-category-${idx}`} className="h-9 rounded-md border-transparent pr-6 focus:border-input" />
          <RowCopy value={r.category} testid={`bulkadd-copy-category-${idx}`} />
        </div>
      </td>
      <td>
        <div className="group/cell relative">
          <Input value={r.code} onChange={(e) => h.setCell(r.key, 'code', e.target.value)}
            onKeyDown={(e) => h.onCodeKeyDown(e, r)} onFocus={() => h.setOrigin(idx, 'code')}
            data-testid={`bulkadd-code-${idx}`} placeholder="e.g. P01 or P01*10"
            className="h-9 rounded-md border-transparent pr-6 uppercase placeholder:normal-case focus:border-input" />
          <RowCopy value={r.code} testid={`bulkadd-copy-code-${idx}`} />
        </div>
      </td>
      <td>{cell('price', 'text-right')}</td>
      <td>{cell('cost', 'text-right')}</td>
      <td>
        <div className="flex items-center gap-1">
          {cell('qty', 'text-center')}
          {parseInt(r.qty || '1', 10) > 1 && (
            <button type="button" onClick={() => h.expandRow(r.key)} data-testid={`bulkadd-expand-${idx}`}
              title={`Split into ${parseInt(r.qty, 10)} coded rows`}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"><CopyPlus className="h-4 w-4" /></button>
          )}
        </div>
      </td>
      <td>
        <Select value={r.store_id} onValueChange={(v) => h.setCell(r.key, 'store_id', v)}>
          <SelectTrigger className="h-9 rounded-md border-transparent text-xs" data-testid={`bulkadd-store-${idx}`}><SelectValue placeholder="Store" /></SelectTrigger>
          <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="px-2" data-testid={`bulkadd-status-${idx}`}>
        {!stKind ? <span className="text-xs text-muted-foreground">—</span> :
          stKind === 'ok' ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Ready</span> :
          stKind === 'duplicate' ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-600">
              <XCircle className="h-3.5 w-3.5" /> {stMsg}
              <button type="button" onClick={() => h.suggestForRow(r.key)} data-testid={`bulkadd-nextfree-${idx}`}
                title="Move to the next free code"
                className="ml-1 inline-flex items-center gap-0.5 rounded-md border border-primary/40 px-1.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary hover:text-primary-foreground"><Wand2 className="h-3 w-3" /> next free</button>
            </span>
          ) : <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> {stMsg}</span>}
      </td>
      <td>
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => h.copyRow(r)} data-testid={`bulkadd-copyrow-${idx}`} title="Copy whole row"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"><Copy className="h-4 w-4" /></button>
          <button onClick={() => h.removeRow(r.key)} data-testid={`bulkadd-remove-${idx}`} className="flex h-8 w-8 items-center justify-center rounded-md text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
        </div>
      </td>
    </tr>
  );
});

export default function BulkAddSheet({ open, onOpenChange, onDone, mode = 'stock', onAddGarments, existingBarcodes = [], defaultCategory = '', invoiceNo = '' }) {
  const { stores } = useApp();
  const isPurchase = mode === 'purchase';
  const targetNoun = isPurchase ? 'purchase' : 'stock';
  const qc = useQueryClient();
  const defaultStore = stores[0]?.id || '';
  const [rows, setRows] = useState(() => Array.from({ length: START_ROWS }, () => blankRow(defaultStore, defaultCategory)));
  const [allStore, setAllStore] = useState(defaultStore);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const rowRefs = useRef({});
  const [highlightKey, setHighlightKey] = useState(null);
  const [stockHits, setStockHits] = useState([]);
  const [stockSearching, setStockSearching] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [size, setSize] = useState('md');    // popup window size preset
  const [search, setSearch] = useState('');  // find a code / name among the rows
  const [dupCodes, setDupCodes] = useState(() => new Set()); // barcodes that already exist in stock
  const [skipAmbiguous, setSkipAmbiguous] = useState(() => {
    try { return localStorage.getItem('bulkadd_skip_ambiguous') === '1'; } catch (e) { return false; }
  });
  const alpha = skipAmbiguous ? SAFE_ALPHA : FULL_ALPHA;
  const toggleSkip = (v) => { setSkipAmbiguous(v); try { localStorage.setItem('bulkadd_skip_ambiguous', v ? '1' : '0'); } catch (e) { /* ignore */ } };
  // Find the next free starting code for `base` such that a block of `qty` sequential
  // codes is all unused (skips taken codes → "jump past P01C").
  const firstFreeStart = (base, qty, taken) => {
    let i = 1;
    for (; i <= 100000; i++) {
      const block = Array.from({ length: qty }, (_, k) => base + numToAlpha(i + k, alpha));
      if (block.every((c) => !taken.has(c))) return base + numToAlpha(i, alpha);
    }
    return base + numToAlpha(i, alpha);
  };
  const origin = useRef({ row: 0, field: 'name' });
  // --- Save & edit later (drafts): stock is NOT added until "Add to stock" ---
  const [draftId, setDraftId] = useState(null);   // set when the sheet was loaded from a draft
  const [title, setTitle] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [autoStatus, setAutoStatus] = useState('idle'); // idle | saving | saved
  const [importing, setImporting] = useState(false);
  const draftIdRef = useRef(null);
  const autoRef = useRef({ inFlight: false, lastSnap: '' });
  const latestRef = useRef({ snap: '', body: null, count: 0 });
  const fileRef = useRef(null);
  useEffect(() => { draftIdRef.current = draftId; }, [draftId]);

  const { data: cats = [] } = useQuery({ queryKey: ['categories'], queryFn: async () => (await api.get('/categories')).data.data, enabled: open });
  const { data: drafts = [] } = useQuery({ queryKey: ['intake-drafts'], queryFn: async () => (await api.get('/stock/intake-drafts')).data.data, enabled: open });

  const reset = () => {
    setRows(Array.from({ length: START_ROWS }, () => blankRow(defaultStore, defaultCategory)));
    setAllStore(defaultStore); setDupCodes(new Set()); setDraftId(null); setTitle('');
    draftIdRef.current = null; autoRef.current = { inFlight: false, lastSnap: '' }; setAutoStatus('idle');
  };
  const setCell = (key, field, val) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, [field]: val } : r)));
  const addRow = () => {
    const nr = blankRow(allStore, defaultCategory);
    setRows((rs) => [...rs, nr]);
    if (defaultCategory) suggestCodeForCategory(nr.key, defaultCategory); // auto next-code for the new row
  };
  const removeRow = (key) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs));
  // Turn a single "code + qty" row into individual coded rows (P01 ×10 -> P01A…P01J),
  // copying name/category/prices/store so each piece is visible and editable.
  const spawnRows = (r, base, n) => {
    const codes = expandCodes(base, n, alpha);
    const clones = codes.map((c) => ({
      key: uid(), sr: r.sr, name: r.name, category: r.category, code: c,
      price: r.price, cost: r.cost, qty: '1', store_id: r.store_id }));
    setRows((rs) => {
      const i = rs.findIndex((x) => x.key === r.key);
      if (i === -1) return rs;
      return [...rs.slice(0, i), ...clones, ...rs.slice(i + 1)];
    });
    toast.success(`Created ${n} rows: ${codes[0]} … ${codes[codes.length - 1]}`);
  };
  const expandRow = (key) => {
    const r = rows.find((x) => x.key === key);
    if (!r) return;
    const n = Math.max(1, parseInt(r.qty || '1', 10) || 1);
    const base = (r.code || '').trim().toUpperCase();
    if (!base) { toast.error('Enter an Item Code first, then tap expand.'); return; }
    if (n <= 1) { toast.error('Set Qty to 2 or more, then tap expand.'); return; }
    spawnRows(r, base, n);
  };
  // Inline command: typing "P01*10" (or "P01 x10", "P01×10") in the code cell and pressing
  // Enter auto-expands that row into P01A…P01J — no button needed.
  const onCodeKeyDown = (e, r) => {
    if (e.key !== 'Enter') return;
    const m = (r.code || '').trim().match(/^(.+?)\s*[*xX×]\s*(\d+)$/);
    if (!m) return;
    e.preventDefault();
    const base = m[1].trim().toUpperCase();
    const n = Math.max(1, parseInt(m[2], 10) || 1);
    if (n <= 1) { setCell(r.key, 'code', base); return; }
    spawnRows(r, base, n);
  };
  // Expand EVERY row that has Qty > 1 and a code — handy right after pasting a list.
  const expandAll = () => {
    const targets = rows.filter((r) => (parseInt(r.qty || '1', 10) || 1) > 1 && (r.code || '').trim());
    if (!targets.length) { toast.error('No rows with Qty > 1 and a code to expand.'); return; }
    let created = 0;
    const out = [];
    for (const r of rows) {
      const n = Math.max(1, parseInt(r.qty || '1', 10) || 1);
      const base = (r.code || '').trim().toUpperCase();
      if (n > 1 && base) {
        expandCodes(base, n, alpha).forEach((c) => out.push({
          key: uid(), sr: r.sr, name: r.name, category: r.category, code: c,
          price: r.price, cost: r.cost, qty: '1', store_id: r.store_id }));
        created += n;
      } else { out.push(r); }
    }
    setRows(out);
    toast.success(`Expanded ${targets.length} row(s) into ${created} coded rows.`);
  };
  // Auto-suggest next free code(s): bump clashing rows past taken codes (in stock + in-sheet).
  const collectTaken = (excludeKey) => {
    const taken = new Set(dupCodes);
    for (const o of rows) {
      if (o.key === excludeKey || !isFilled(o) || !(o.code || '').trim()) continue;
      const q = Math.max(1, parseInt(o.qty || '1', 10) || 1);
      expandCodes(o.code.trim().toUpperCase(), q, alpha).forEach((c) => taken.add(c));
    }
    return taken;
  };
  const suggestForRow = (key) => {
    const r = rows.find((x) => x.key === key);
    if (!r || !(r.code || '').trim()) return;
    const q = Math.max(1, parseInt(r.qty || '1', 10) || 1);
    const next = firstFreeStart(baseOf(r.code.trim().toUpperCase()), q, collectTaken(key));
    setCell(key, 'code', next);
    toast.success(`Moved to next free code: ${next}${q > 1 ? ` (+${q - 1})` : ''}`);
  };
  const suggestFreeCodes = () => {
    const out = rows.map((r) => ({ ...r }));
    const taken = new Set(dupCodes);
    // Reserve codes of rows that are NOT clashing so we don't reuse them.
    for (const r of out) {
      if (!isFilled(r) || !(r.code || '').trim()) continue;
      const st = validation.status[r.key];
      if (st && st.kind !== 'duplicate') {
        const q = Math.max(1, parseInt(r.qty || '1', 10) || 1);
        expandCodes(r.code.trim().toUpperCase(), q, alpha).forEach((c) => taken.add(c));
      }
    }
    let fixed = 0;
    for (const r of out) {
      if (!isFilled(r) || !(r.code || '').trim()) continue;
      const st = validation.status[r.key];
      if (!st || st.kind !== 'duplicate') continue;
      const q = Math.max(1, parseInt(r.qty || '1', 10) || 1);
      const next = firstFreeStart(baseOf(r.code.trim().toUpperCase()), q, taken);
      r.code = next;
      expandCodes(next, q, alpha).forEach((c) => taken.add(c));
      fixed++;
    }
    if (!fixed) { toast.error('No clashing rows to fix.'); return; }
    setRows(out);
    toast.success(`Bumped ${fixed} row(s) to the next free code.`);
  };
  const applyStoreAll = (sid) => { setAllStore(sid); setRows((rs) => rs.map((r) => ({ ...r, store_id: sid }))); };

  // One-tap copy for any cell / whole row (no selecting needed).
  const copyText = async (text) => {
    const val = String(text ?? '');
    if (!val.trim()) { toast.error('Nothing to copy'); return; }
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(val);
      else {
        const ta = document.createElement('textarea');
        ta.value = val; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
      }
      toast.success(`Copied: ${val.length > 28 ? val.slice(0, 28) + '…' : val}`);
    } catch { toast.error('Copy failed'); }
  };
  // Row copied in the same tab-separated order the sheet pastes back in.
  const copyRow = (r) => copyText([r.name, r.category, r.code, r.price, r.cost, r.qty || '1'].map((x) => String(x ?? '')).join('\t'));
  const CopyBtn = ({ value, testid }) => (value ? (
    <button type="button" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => copyText(value)}
      data-testid={testid} title="Copy"
      className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 hover:text-foreground focus-visible:opacity-100">
      <Copy className="h-3 w-3" />
    </button>
  ) : null);


  // --- Bulk price edit (multi-select rows, set one price on all) ---
  const [selected, setSelected] = useState(() => new Set());
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkStore, setBulkStore] = useState('');
  const toggleSel = (key) => setSelected((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const applyBulkPrice = (field) => {
    const val = bulkPrice.trim();
    if (val === '' || isNaN(Number(val)) || Number(val) < 0) { toast.error('Enter a valid price first.'); return; }
    setRows((rs) => rs.map((r) => (selected.has(r.key) ? { ...r, [field]: val } : r)));
    toast.success(`Set ${field === 'price' ? 'sale' : 'purchase'} price ₹${val} on ${selected.size} row(s).`);
  };
  const applyBulkStore = () => {
    const sid = bulkStore || defaultStore;
    if (!sid) { toast.error('Pick a store first.'); return; }
    setRows((rs) => rs.map((r) => (selected.has(r.key) ? { ...r, store_id: sid } : r)));
    const name = stores.find((s) => s.id === sid)?.name || 'store';
    toast.success(`Moved ${selected.size} row(s) to ${name}.`);
  };

  // --- Auto next code per category: pick a category -> suggest its next design code ---
  const suggestCodeForCategory = async (key, category) => {
    const cat = (category || '').trim();
    if (!cat) return;
    const r = rows.find((x) => x.key === key);
    if (r && (r.code || '').trim()) return; // never overwrite a code the user typed
    let info;
    try {
      info = await qc.fetchQuery({ queryKey: ['next-code', cat], staleTime: 30000,
        queryFn: async () => (await api.get('/catalog/next-code', { params: { category: cat } })).data.data });
    } catch (e) { return; }
    if (!info || info.number == null || !info.next_code) return;
    const prefix = info.prefix || '';
    const width = info.width || String(info.number).length;
    let maxNum = info.number;
    const esc = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp('^' + esc + '(\\d+)[A-Za-z]*$', 'i');
    for (const o of rows) {
      if (o.key === key || (o.category || '').trim().toLowerCase() !== cat.toLowerCase()) continue;
      const m = (o.code || '').trim().match(re);
      if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
    }
    setCell(key, 'code', prefix + String(maxNum + 1).padStart(width, '0') + 'A');
  };

  const isFilled = (r) => r.name || r.code || r.price || r.cost || (r.qty && r.qty !== '1');

  const initedRef = useRef(false);
  // Prefill blank rows' Category (purchase mode) AND auto-generate the first row's next
  // code, so opening the sheet gives you one ready-to-fill row with a code already in place.
  useEffect(() => {
    if (!open) { initedRef.current = false; return; }
    if (!defaultCategory || initedRef.current) return;
    initedRef.current = true;
    setRows((rs) => rs.map((r) => (isFilled(r) || (r.category && r.category.trim())) ? r : { ...r, category: defaultCategory }));
    const first = rows[0];
    if (first && !(first.code || '').trim()) suggestCodeForCategory(first.key, defaultCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultCategory]);

  // Shared helpers so autosave, manual save, resume and import all serialise identically.
  const rowToDraft = (r) => ({ sr: r.sr, name: r.name, category: r.category, code: r.code, price: r.price, cost: r.cost, qty: r.qty, store_id: r.store_id });
  const snapOf = (payloadRows, storeId, titleStr) => JSON.stringify({ payloadRows, allStore: storeId, title: (titleStr || '').trim() });

  // ---- paste from Excel ----
  const handlePaste = (e) => {
    const text = (e.clipboardData || window.clipboardData)?.getData('text') || '';
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return;
    e.preventDefault();
    let lines = text.replace(/\r/g, '').split('\n');
    if (lines.length && lines[lines.length - 1] === '') lines.pop();
    if (!lines.length) return;
    const startField = Math.max(0, FIELD_ORDER.indexOf(origin.current.field));
    const startRow = origin.current.row;
    setRows((rs) => {
      const next = rs.map((r) => ({ ...r }));
      while (next.length < startRow + lines.length) next.push(blankRow(allStore, defaultCategory));
      lines.forEach((line, li) => {
        const cells = line.split('\t');
        const target = next[startRow + li];
        cells.forEach((cell, ci) => {
          const fIdx = startField + ci;
          if (fIdx < FIELD_ORDER.length) target[FIELD_ORDER[fIdx]] = String(cell).trim();
        });
      });
      return next;
    });
    toast.success(`Pasted ${lines.length} row${lines.length === 1 ? '' : 's'} — check the validation summary below`);
  };

  // ---- live validation ----
  const validation = useMemo(() => {
    const freq = {}; const rowCodes = {};
    for (const r of rows) {
      if (!isFilled(r)) continue;
      const q = Math.max(1, parseInt(r.qty || '1', 10) || 1);
      const codes = r.code.trim() ? expandCodes(r.code.trim().toUpperCase(), q, alpha) : [];
      rowCodes[r.key] = codes;
      codes.forEach((c) => { freq[c] = (freq[c] || 0) + 1; });
    }
    const status = {}; let valid = 0, incomplete = 0, duplicate = 0;
    for (const r of rows) {
      if (!isFilled(r)) { status[r.key] = null; continue; }
      const miss = [];
      if (!r.name.trim()) miss.push('name');
      if (!r.code.trim()) miss.push('code');
      if (!r.price || Number(r.price) <= 0) miss.push('sale price');
      if (r.cost === '' || r.cost == null || Number(r.cost) < 0 || isNaN(Number(r.cost))) miss.push('purchase price');
      if (!r.store_id) miss.push('store');
      const q = parseInt(r.qty || '1', 10);
      if (r.qty && (isNaN(q) || q < 1)) miss.push('qty');
      const codes = rowCodes[r.key] || [];
      const dupHitsSheet = codes.filter((c) => freq[c] > 1);
      const dupHitsDb = codes.filter((c) => dupCodes.has(c));
      const fmt = (arr) => `${arr.slice(0, 3).join(', ')}${arr.length > 3 ? ` +${arr.length - 3}` : ''}`;
      if (miss.length) { status[r.key] = { kind: 'incomplete', msg: 'Missing: ' + miss.join(', ') }; incomplete++; }
      else if (dupHitsDb.length) { status[r.key] = { kind: 'duplicate', msg: `Already in stock: ${fmt(dupHitsDb)}` }; duplicate++; }
      else if (dupHitsSheet.length) { status[r.key] = { kind: 'duplicate', msg: `Duplicate in sheet: ${fmt(dupHitsSheet)}` }; duplicate++; }
      else { status[r.key] = { kind: 'ok' }; valid++; }
    }
    return { status, valid, incomplete, duplicate, total: valid + incomplete + duplicate };
  }, [rows, dupCodes, alpha]);

  // ---- debounced duplicate check against existing stock ----
  useEffect(() => {
    if (!open) return;
    const codes = new Set();
    for (const r of rows) {
      if (!isFilled(r) || !r.code.trim()) continue;
      const q = Math.max(1, parseInt(r.qty || '1', 10) || 1);
      expandCodes(r.code.trim().toUpperCase(), q, alpha).forEach((c) => codes.add(c));
    }
    if (codes.size === 0) { setDupCodes(new Set()); return; }
    const t = setTimeout(async () => {
      try {
        const ex = (await api.post('/garments/check-barcodes', { barcodes: [...codes] })).data.data.existing || {};
        setDupCodes(new Set(Object.keys(ex)));
      } catch (e) { /* offline: server still enforces uniqueness on save */ }
    }, 450);
    return () => clearTimeout(t);
  }, [rows, open, alpha]);

  const totalPieces = useMemo(() => rows.reduce((a, r) => (isFilled(r) ? a + Math.max(1, parseInt(r.qty || '1', 10) || 1) : a), 0), [rows]);

  const save = async () => {
    if (savingRef.current) return;              // re-entry / double-submit guard
    if (validation.total === 0) { toast.error('Enter at least one item row.'); return; }
    if (validation.incomplete > 0 || validation.duplicate > 0) {
      toast.error(`Fix ${validation.incomplete} incomplete and ${validation.duplicate} duplicate row(s) highlighted below.`);
      return;
    }
    const garments = [];
    for (const r of rows) {
      if (!isFilled(r)) continue;
      const q = Math.max(1, parseInt(r.qty || '1', 10) || 1);
      for (const bc of expandCodes(r.code.trim().toUpperCase(), q, alpha)) {
        garments.push({ barcode: bc, product: r.name.trim(), category: r.category.trim(),
          price: Number(r.price), cost: Number(r.cost), store_id: r.store_id, size: '', colour: '',
          sr_no: (r.sr || '').trim() });
      }
    }
    // Fresh duplicate check against live stock — catches codes typed within the debounce
    // window so an already-existing (expanded) code is always flagged before saving.
    try {
      const ex = (await api.post('/garments/check-barcodes', { barcodes: garments.map((g) => g.barcode) })).data.data.existing || {};
      const hits = Object.keys(ex);
      if (hits.length) {
        setDupCodes((prev) => new Set([...prev, ...hits]));
        toast.error(`${hits.length} code(s) already in stock: ${hits.slice(0, 5).join(', ')}${hits.length > 5 ? ` +${hits.length - 5}` : ''}. Fix the highlighted rows.`);
        return;
      }
    } catch (e) { /* offline — the server still enforces uniqueness on save */ }
    // Purchase mode: hand the built garments back to the parent wizard instead of
    // committing to stock directly (the purchase's Confirm step creates them).
    if (isPurchase) {
      const clash = garments.find((g) => existingBarcodes.includes(g.barcode));
      if (clash) { toast.error(`"${clash.barcode}" is already in this purchase's list.`); return; }
      onAddGarments?.(garments);
      toast.success(`${garments.length} item${garments.length === 1 ? '' : 's'} added to the purchase`);
      if (draftId) {
        try { await api.delete(`/stock/intake-drafts/${draftId}`); } catch (e) { /* non-fatal */ }
        qc.invalidateQueries({ queryKey: ['intake-drafts'] });
      }
      reset();
      onDone?.();
      onOpenChange(false);
      return;
    }
    setSaving(true);
    savingRef.current = true;
    try {
      const res = await api.post('/stock/intake', { garments, supplier_id: null, client_txn_id: uid() });
      toast.success(`${res.data.data.garments_created} items added to stock`);
      // Finalised — remove the source draft (if this sheet was resumed from one).
      if (draftId) {
        try { await api.delete(`/stock/intake-drafts/${draftId}`); } catch (e) { /* non-fatal */ }
        qc.invalidateQueries({ queryKey: ['intake-drafts'] });
      }
      qc.invalidateQueries({ queryKey: ['garments'] });
      qc.invalidateQueries({ queryKey: ['stock-summary'] });
      qc.invalidateQueries({ queryKey: ['categories'] });
      reset();
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); savingRef.current = false; }
  };

  // Save the current grid as a draft WITHOUT adding any stock. Resume later to edit/finalise.
  const saveDraft = async () => {
    const payloadRows = rows.filter(isFilled).map(rowToDraft);
    if (!payloadRows.length) { toast.error('Nothing to save yet — add at least one item.'); return; }
    setSavingDraft(true);
    try {
      const body = { rows: payloadRows, all_store: allStore, title: title.trim() };
      const res = draftId
        ? await api.put(`/stock/intake-drafts/${draftId}`, body)
        : await api.post('/stock/intake-drafts', body);
      const d = res.data.data;
      draftIdRef.current = d.id; setDraftId(d.id); setTitle(d.title || '');
      autoRef.current.lastSnap = snapOf(payloadRows, allStore, d.title || title);
      setAutoStatus('saved');
      qc.invalidateQueries({ queryKey: ['intake-drafts'] });
      toast.success('Saved as draft — stock NOT added yet. Resume anytime from Drafts.');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingDraft(false); }
  };

  const resumeDraft = async (id) => {
    try {
      const d = (await api.get(`/stock/intake-drafts/${id}`)).data.data;
      const loaded = (d.rows || []).map((r) => ({
        key: uid(), sr: r.sr || '', name: r.name || '', category: r.category || '', code: r.code || '',
        price: r.price || '', cost: r.cost || '', qty: r.qty || '1',
        store_id: r.store_id || d.all_store || defaultStore }));
      while (loaded.length < START_ROWS) loaded.push(blankRow(d.all_store || defaultStore, defaultCategory));
      setRows(loaded);
      setAllStore(d.all_store || defaultStore);
      draftIdRef.current = d.id; setDraftId(d.id); setTitle(d.title || '');
      // Seed lastSnap so autosave doesn't immediately re-save unchanged loaded data.
      autoRef.current.lastSnap = snapOf(loaded.filter(isFilled).map(rowToDraft), d.all_store || defaultStore, d.title);
      setAutoStatus('saved');
      setDraftsOpen(false);
      toast.success('Draft loaded — edit then press "Add to stock" to finalise.');
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteDraft = async (id) => {
    try {
      await api.delete(`/stock/intake-drafts/${id}`);
      if (id === draftId) { setDraftId(null); draftIdRef.current = null; autoRef.current.lastSnap = ''; setAutoStatus('idle'); }
      qc.invalidateQueries({ queryKey: ['intake-drafts'] });
      toast.success('Draft deleted');
    } catch (e) { toast.error(errMsg(e)); }
  };

  // ---- Auto-save: silently persist the grid a couple of seconds after edits so
  // nothing is lost if the sheet is closed. Never adds stock; only saves a draft.
  useEffect(() => {
    const payloadRows = rows.filter(isFilled).map(rowToDraft);
    latestRef.current = {
      snap: snapOf(payloadRows, allStore, title),
      body: { rows: payloadRows, all_store: allStore, title: title.trim() },
      count: payloadRows.length,
    };
  });

  const autosaveNow = useCallback(async () => {
    if (!open || saving) return;
    const cur = latestRef.current;
    if (!cur.count) return;
    if (cur.snap === autoRef.current.lastSnap) return;
    if (autoRef.current.inFlight) return; // a later run will pick up the newest state
    autoRef.current.inFlight = true;
    setAutoStatus('saving');
    try {
      const res = draftIdRef.current
        ? await api.put(`/stock/intake-drafts/${draftIdRef.current}`, cur.body)
        : await api.post('/stock/intake-drafts', cur.body);
      const d = res.data.data;
      draftIdRef.current = d.id; setDraftId(d.id);
      autoRef.current.lastSnap = cur.snap;
      setAutoStatus('saved');
      qc.invalidateQueries({ queryKey: ['intake-drafts'] });
    } catch (e) {
      setAutoStatus('idle');
    } finally {
      autoRef.current.inFlight = false;
      // More edits landed while saving? schedule one more pass.
      if (latestRef.current.snap !== autoRef.current.lastSnap && latestRef.current.count) {
        setTimeout(() => { autosaveNow(); }, 400);
      }
    }
  }, [open, saving, qc]);

  useEffect(() => {
    if (!open || saving) return undefined;
    const t = setTimeout(() => { autosaveNow(); }, 2500);
    return () => clearTimeout(t);
  }, [rows, allStore, title, open, saving, autosaveNow]);

  // Warn before accidental tab close/refresh while there is unsaved entry work.
  useEffect(() => {
    const hasWork = open && rows.some(isFilled);
    if (!hasWork) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [open, rows]);

  // Debounced, server-side search of EXISTING stock (never downloads whole inventory).
  const searchQ = search.trim();
  useEffect(() => {
    if (!open || searchQ.length < 2) { setStockHits([]); setStockSearching(false); return undefined; }
    let active = true;
    setStockSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/garments', { params: { q: searchQ, limit: 8 } });
        if (active) setStockHits(r.data.data || []);
      } catch { if (active) setStockHits([]); }
      finally { if (active) setStockSearching(false); }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [searchQ, open]);

  const jumpToRow = useCallback((key) => {
    const el = rowRefs.current[key];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightKey(key);
      setTimeout(() => setHighlightKey((k) => (k === key ? null : k)), 2200);
    }
  }, []);

  // Stable handler bag passed to memoized rows (contents refreshed every render,
  // object identity stays constant so only the EDITED row re-renders).
  const handlersRef = useRef({});
  handlersRef.current = {
    setCell, removeRow, expandRow, copyRow, toggleSel, suggestForRow,
    suggestCodeForCategory, onCodeKeyDown, cats,
    setOrigin: (row, field) => { origin.current = { row, field }; },
    setRowRef: (key, el) => { rowRefs.current[key] = el; },
  };

  // ---- Import an Excel/CSV file: it lands as a DRAFT for review (no stock added). ----
  const numClean = (v) => { const s = String(v ?? '').trim(); return /^-?\d+\.0+$/.test(s) ? s.split('.')[0] : s; };
  const onImportFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/stock/parse-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const parsed = r.data.data.rows || [];
      const cols = r.data.data.columns || [];
      if (!parsed.length) { toast.error('The file has no rows.'); return; }
      const used = new Set();
      const pick = (...tests) => {
        for (const t of tests) {
          const hit = cols.find((c) => !used.has(c) && t.test(String(c).toLowerCase()));
          if (hit) { used.add(hit); return hit; }
        }
        return null;
      };
      const mCode = pick(/item\s*code/, /barcode/, /\bsku\b/, /\bcode\b/);
      const mCost = pick(/purchase\s*price/, /\bcost\b/, /\bbuy/, /purchase/);
      const mPrice = pick(/sale\s*price/, /selling/, /\bmrp\b/, /\bprice\b/, /\brate\b/);
      const mQty = pick(/\bqty\b/, /quantit/, /\bpcs\b/, /pieces/, /\bnos?\b/);
      const mCat = pick(/categ/, /\btype\b/, /group/);
      const mName = pick(/product/, /\bname\b/, /descrip/, /design/, /item/);
      const mSr = pick(/sr\s*no/, /serial/, /invoice\s*no/, /\bsr\b/, /\bs\.?\s*no\b/);
      const gridRows = [];
      for (const row of parsed) {
        const gr = {
          sr: mSr ? String(row[mSr] ?? '').trim() : '',
          name: mName ? String(row[mName] ?? '').trim() : '',
          category: (mCat ? String(row[mCat] ?? '').trim() : '') || defaultCategory,
          code: (mCode ? numClean(row[mCode]) : '').toUpperCase(),
          price: mPrice ? numClean(row[mPrice]) : '',
          cost: mCost ? numClean(row[mCost]) : '',
          qty: (mQty ? numClean(row[mQty]) : '') || '1',
          store_id: allStore || defaultStore,
        };
        if (gr.name || gr.code || gr.price || gr.cost) gridRows.push(gr);
      }
      if (!gridRows.length) { toast.error('Could not detect item columns. Expected Name, Item Code, Sale Price, Purchase Price.'); return; }
      const display = gridRows.map((g) => ({ key: uid(), ...g }));
      while (display.length < START_ROWS) display.push(blankRow(allStore, defaultCategory));
      setRows(display);
      // Persist immediately as a draft for review.
      const draftTitle = `Import · ${file.name}`.slice(0, 120);
      const body = { rows: gridRows, all_store: allStore, title: draftTitle };
      const d = (await api.post('/stock/intake-drafts', body)).data.data;
      draftIdRef.current = d.id; setDraftId(d.id); setTitle(d.title || '');
      autoRef.current.lastSnap = snapOf(gridRows, allStore, d.title);
      setAutoStatus('saved');
      qc.invalidateQueries({ queryKey: ['intake-drafts'] });
      toast.success(`Imported ${gridRows.length} row(s) as a draft — review the highlighted rows, then press "Add to stock".`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const rowAccent = (st) => (!st || st.kind === 'ok') ? '' : (st.kind === 'duplicate' ? 'bg-rose-50/60' : 'bg-amber-50/60');
  const hasErrors = validation.incomplete > 0 || validation.duplicate > 0;
  const hasMulti = rows.some((r) => (parseInt(r.qty || '1', 10) || 1) > 1 && (r.code || '').trim());
  // Search across the rows by code or name (case-insensitive).
  const q = search.trim().toLowerCase();
  const matchRow = (r) => !q || (r.code || '').toLowerCase().includes(q) || (r.name || '').toLowerCase().includes(q);
  const matchCount = q ? rows.filter((r) => isFilled(r) && matchRow(r)).length : 0;
  const rowMatches = q ? rows.map((r, idx) => ({ r, idx })).filter(({ r }) => isFilled(r) && matchRow(r)) : [];

  // --- deterministic design-group tinting --------------------------------------
  // Rows that share a code base (prefix+number; trailing sequence letters stripped,
  // e.g. P01A/P01B/P01C -> "P01") belong to ONE design group and get the SAME subtle
  // full-row tint. Tints are assigned by the group's order of first appearance
  // (group 01 -> tint 0, group 02 -> tint 1, ...) then cycle through the palette, so
  // adjacent groups never share a tint and the sequence is stable/deterministic while
  // editing. Error/duplicate/selected/highlight states override the tint (see BulkRow).
  // Frontend-only + O(rows): no extra DB/API calls.
  const groupKey = (code) => (code || '').toUpperCase().trim().replace(/[A-Z]+$/, '');
  const GROUP_TINTS = [
    { bg: 'bg-sky-500/10', bd: 'border-l-sky-400' },
    { bg: 'bg-violet-500/10', bd: 'border-l-violet-400' },
    { bg: 'bg-cyan-500/10', bd: 'border-l-cyan-400' },
    { bg: 'bg-fuchsia-500/10', bd: 'border-l-fuchsia-400' },
    { bg: 'bg-indigo-500/10', bd: 'border-l-indigo-400' },
    { bg: 'bg-blue-500/10', bd: 'border-l-blue-400' },
  ];
  const groupMeta = useMemo(() => {
    const indexOf = {};
    let order = 0;
    let prevKey = null;
    const meta = {};
    for (const r of rows) {
      const gk = groupKey(r.code);
      if (!gk) {
        meta[r.key] = { bg: '', bd: 'border-l-transparent', start: false };
        prevKey = null;
        continue;
      }
      if (!(gk in indexOf)) { indexOf[gk] = order; order += 1; }
      const t = GROUP_TINTS[indexOf[gk] % GROUP_TINTS.length];
      meta[r.key] = { bg: t.bg, bd: t.bd, start: prevKey !== null && gk !== prevKey };
      prevKey = gk;
    }
    return meta;
  }, [rows]);
  const storeName = (id) => (stores || []).find((s) => s.id === id)?.name || id;


  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className={`${SIZE[size].w} max-h-[92vh] overflow-y-auto`}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-6">
            <DialogTitle className="flex items-center gap-2"><Table2 className="h-5 w-5 text-primary" /> Bulk Add Items</DialogTitle>
            <div className="flex items-center gap-0.5 rounded-lg border border-border p-0.5" data-testid="bulkadd-size-control" title="Resize this window">
              {Object.entries(SIZE).map(([k, cfg]) => (
                <button key={k} type="button" onClick={() => setSize(k)} data-testid={`bulkadd-size-${k}`}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${size === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'}`}>{cfg.label}</button>
              ))}
            </div>
          </div>
          <DialogDescription>Paste rows from Excel, or type a code like <b>P01*10</b> in the Item Code cell and press Enter to auto-create P01A…P01J. You can also set Qty and tap expand (or <b>Expand all&nbsp;×qty</b>). After expanding, edit the <b>Sale Price</b> on any single piece to price it specially.</DialogDescription>
        </DialogHeader>

        {isPurchase && invoiceNo ? (
          <div className="flex items-center gap-2 rounded-lg bg-secondary/60 px-3 py-1.5 text-xs" data-testid="bulkadd-invoice-no">
            <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span>Supplier invoice <b className="font-mono">{invoiceNo}</b> — applies to every item in this batch.</span>
          </div>
        ) : null}

        <div className="flex items-start gap-2 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3 text-xs text-muted-foreground" data-testid="bulkadd-paste-hint">
          <ClipboardPaste className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>Paste columns in this order: <b>Name · Category · Item Code · Sale Price · Purchase Price · Quantity</b>. Then set the store below. Purchase price is required.</span>
        </div>

        {/* Draft toolbar — save progress without adding stock, or resume a saved draft */}
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-muted/40 p-2">
          <div className="relative min-w-[160px] flex-1">
            <Pencil className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Draft name (optional)"
              className="h-9 rounded-lg pl-8 text-sm" data-testid="bulkadd-draft-title" />
          </div>
          {autoStatus !== 'idle' && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground" data-testid="bulkadd-autosave-status">
              {autoStatus === 'saving'
                ? <><Loader2 className="h-3 w-3 animate-spin" /> Auto-saving…</>
                : <><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Draft saved</>}
            </span>
          )}
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={(e) => onImportFile(e.target.files?.[0])} data-testid="bulkadd-import-input" />
          <Button variant="outline" className="h-9 rounded-lg" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="bulkadd-import-open">
            {importing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Import file
          </Button>
          <Button variant="outline" className="h-9 rounded-lg" onClick={() => setDraftsOpen(true)} data-testid="bulkadd-drafts-open">
            <FolderOpen className="mr-1.5 h-4 w-4" /> Drafts{drafts.length ? ` (${drafts.length})` : ''}
          </Button>
          <Button variant="secondary" className="h-9 rounded-lg" onClick={saveDraft} disabled={savingDraft} data-testid="bulkadd-save-draft">
            {savingDraft ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            {draftId ? 'Update draft' : 'Save & edit later'}
          </Button>
        </div>
        {draftId && (
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 ring-1 ring-amber-200" data-testid="bulkadd-editing-draft">
            <FileText className="h-3.5 w-3.5 shrink-0" />
            <span>Editing a saved draft — stock is <b>not</b> added yet. Press "Add to stock" below to finalise, or "Update draft" to keep saving.</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Allocate all to</span>
          <Select value={allStore} onValueChange={applyStoreAll}>
            <SelectTrigger className="h-9 w-[160px] rounded-lg" data-testid="bulkadd-allstore"><SelectValue /></SelectTrigger>
            <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          {hasMulti && (
            <Button variant="outline" className="h-9 rounded-lg" onClick={expandAll} data-testid="bulkadd-expand-all">
              <CopyPlus className="mr-1.5 h-4 w-4" /> Expand all ×qty
            </Button>
          )}
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border px-2.5 py-1.5 text-xs" title="Generated codes will skip the look-alike letters I and O">
            <Switch checked={skipAmbiguous} onCheckedChange={toggleSkip} data-testid="bulkadd-skip-ambiguous" />
            <span className="font-medium">Skip O / I</span>
          </label>
          <div className="relative ml-auto" data-testid="bulkadd-search-wrap">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find code in sheet or stock…"
              className="h-9 w-[220px] rounded-lg pl-8 text-sm" data-testid="bulkadd-search" />
            {q && (
              <button type="button" onClick={() => setSearch('')} data-testid="bulkadd-search-clear"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"><XCircle className="h-3.5 w-3.5" /></button>
            )}
            {q && (
              <div className="absolute right-0 z-30 mt-1 max-h-[320px] w-[320px] overflow-auto rounded-xl border border-border bg-popover p-2 shadow-lg" data-testid="bulkadd-search-panel">
                {(() => {
                  const upper = searchQ.toUpperCase();
                  const exactSheet = rows.filter((r) => isFilled(r) && (r.code || '').toUpperCase() === upper);
                  const exactStock = stockHits.find((h) => (h.barcode || '').toUpperCase() === upper);
                  return (
                    <>
                      {exactSheet.length > 1 && (
                        <div className="mb-1.5 flex items-start gap-1.5 rounded-lg bg-rose-50 px-2 py-1.5 text-[11px] font-medium text-rose-700" data-testid="bulkadd-search-dup-sheet">
                          <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> “{upper}” is entered {exactSheet.length} times in this purchase.
                        </div>
                      )}
                      {exactStock && (
                        <div className="mb-1.5 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-800" data-testid="bulkadd-search-dup-stock">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> “{exactStock.barcode}” already exists in stock ({storeName(exactStock.store_id)} / {exactStock.status}).
                        </div>
                      )}
                      <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">In this purchase ({rowMatches.length})</p>
                      {rowMatches.length === 0 ? (
                        <p className="px-1 pb-1.5 text-[11px] text-muted-foreground">No matching rows.</p>
                      ) : rowMatches.slice(0, 20).map(({ r, idx }) => {
                        const st = validation.status[r.key];
                        return (
                          <button key={r.key} type="button" onClick={() => jumpToRow(r.key)} data-testid={`bulkadd-search-jump-${idx}`}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-[12px] hover:bg-muted">
                            <span className="w-7 shrink-0 text-muted-foreground">#{idx + 1}</span>
                            <span className="font-mono font-medium">{r.code || '—'}</span>
                            {st?.kind === 'duplicate' && <span className="rounded bg-rose-100 px-1 text-[10px] font-semibold text-rose-700">dup</span>}
                            <span className="ml-auto text-[10px] text-primary">Jump →</span>
                          </button>
                        );
                      })}
                      <p className="mt-1 border-t border-border px-1 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Already in stock {stockSearching ? '(searching…)' : `(${stockHits.length})`}
                      </p>
                      {!stockSearching && stockHits.length === 0 ? (
                        <p className="px-1 pb-1 text-[11px] text-muted-foreground">Not found in stock.</p>
                      ) : stockHits.map((h) => (
                        <div key={h.id} data-testid={`bulkadd-search-stock-${h.barcode}`}
                          className="flex items-center gap-2 rounded-md px-2 py-1 text-[12px]">
                          <span className="font-mono font-medium">{h.barcode}</span>
                          <span className="ml-auto text-[10px] text-muted-foreground">{storeName(h.store_id)} · {h.status}</span>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          {q && <span className="text-xs text-muted-foreground" data-testid="bulkadd-search-count">{matchCount} in sheet</span>}
          <Button variant="ghost" className="h-9 rounded-lg text-muted-foreground" onClick={() => setClearOpen(true)} data-testid="bulkadd-clear-open"><Eraser className="mr-1 h-4 w-4" /> Clear All</Button>
        </div>

        {/* Validation summary */}
        {validation.total > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="bulkadd-validation-summary">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700 ring-1 ring-emerald-200" data-testid="valid-count"><CheckCircle2 className="h-3.5 w-3.5" /> {validation.valid} valid</span>
            {validation.incomplete > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-700 ring-1 ring-amber-200" data-testid="incomplete-count"><AlertTriangle className="h-3.5 w-3.5" /> {validation.incomplete} incomplete</span>}
            {validation.duplicate > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 font-medium text-rose-700 ring-1 ring-rose-200" data-testid="duplicate-count"><XCircle className="h-3.5 w-3.5" /> {validation.duplicate} duplicate</span>}
            {validation.duplicate > 0 && (
              <button onClick={suggestFreeCodes} data-testid="bulkadd-suggest-free"
                className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-2.5 py-1 font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground">
                <Wand2 className="h-3.5 w-3.5" /> Bump to next free
              </button>
            )}
            {hasErrors && <span className="text-muted-foreground">Highlighted rows need attention before saving.</span>}
          </div>
        )}

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 p-2" data-testid="bulkadd-bulkprice-bar">
            <span className="text-sm font-medium text-primary">{selected.size} selected</span>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">₹</span>
              <Input value={bulkPrice} onChange={(e) => setBulkPrice(e.target.value)} placeholder="0" inputMode="decimal"
                className="h-9 w-28 rounded-lg pl-6 text-right" data-testid="bulkadd-bulkprice-input" />
            </div>
            <Button variant="secondary" className="h-9 rounded-lg" onClick={() => applyBulkPrice('price')} data-testid="bulkadd-apply-sale">Set sale price</Button>
            <Button variant="secondary" className="h-9 rounded-lg" onClick={() => applyBulkPrice('cost')} data-testid="bulkadd-apply-cost">Set purchase price</Button>
            <div className="flex items-center gap-1.5">
              <Select value={bulkStore || defaultStore} onValueChange={setBulkStore}>
                <SelectTrigger className="h-9 w-[140px] rounded-lg" data-testid="bulkadd-bulkstore"><SelectValue /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="secondary" className="h-9 rounded-lg" onClick={applyBulkStore} data-testid="bulkadd-apply-store">Move to store</Button>
            </div>
            <Button variant="ghost" className="ml-auto h-9 rounded-lg text-muted-foreground" onClick={() => setSelected(new Set())} data-testid="bulkadd-clear-selection">Clear selection</Button>
          </div>
        )}

        <div className={`${SIZE[size].h} overflow-auto rounded-xl border border-border`} onPaste={handlePaste}>
          <table className="w-full min-w-[960px] border-collapse text-sm" data-testid="bulkadd-grid">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr className="[&>th]:border-b [&>th]:border-border [&>th]:px-2 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold">
                <th className="w-8 text-center"><Checkbox
                  checked={rows.some(isFilled) && rows.filter(isFilled).every((r) => selected.has(r.key))}
                  onCheckedChange={(v) => setSelected(v ? new Set(rows.filter(isFilled).map((r) => r.key)) : new Set())}
                  data-testid="bulkadd-select-all" /></th>
                <th className="w-8">#</th>
                <th className="w-[96px]">Sr No</th>
                <th className="min-w-[170px]">Name</th>
                <th className="min-w-[120px]">Category</th>
                <th className="min-w-[110px]">Item Code</th>
                <th className="w-[140px]">Sale Price</th>
                <th className="w-[130px]">Purchase Price</th>
                <th className="w-[96px]">Qty</th>
                <th className="w-[120px]">Store</th>
                <th className="min-w-[150px]">Status</th>
                <th className="w-[74px]"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const st = validation.status[r.key];
                const gm = groupMeta[r.key] || { bg: '', bd: 'border-l-transparent', start: false };
                return (
                  <BulkRow key={r.key} row={r} idx={idx}
                    stKind={st ? st.kind : undefined} stMsg={st ? st.msg : undefined}
                    isSelected={selected.has(r.key)} isHighlighted={highlightKey === r.key}
                    groupBg={gm.bg} groupBorder={gm.bd} groupStart={gm.start} filled={isFilled(r)}
                    accent={st && st.kind !== 'ok' ? (st.kind === 'duplicate' ? 'bg-rose-50/60' : 'bg-amber-50/60') : ''}
                    stores={stores} cats={cats} H={handlersRef} />
                );
              })}
            </tbody>
          </table>
          <datalist id="bulkadd-cats">{cats.map((c) => <option key={c.name} value={c.name} />)}</datalist>
        </div>

        <div className="flex items-center justify-between">
          <Button variant="secondary" className="h-9 rounded-lg" onClick={addRow} data-testid="bulkadd-add-row"><Plus className="mr-1 h-4 w-4" /> Add Row</Button>
          <span className="text-sm text-muted-foreground" data-testid="bulkadd-summary">{validation.total} item{validation.total === 1 ? '' : 's'} · {totalPieces} piece{totalPieces === 1 ? '' : 's'}</span>
        </div>

        <DialogFooter>
          <Button variant="secondary" className="rounded-lg" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button className="rounded-lg" onClick={save} disabled={saving} data-testid="bulkadd-save" title={hasErrors ? 'Fix highlighted rows first' : ''}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (hasErrors ? `Fix ${validation.incomplete + validation.duplicate} row(s) to save` : `Add ${totalPieces} to ${targetNoun}`)}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all rows?</AlertDialogTitle>
            <AlertDialogDescription>This empties the sheet. Nothing is saved yet.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="bulkadd-clear-confirm" onClick={() => { reset(); setClearOpen(false); }}>Clear All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Saved drafts — resume editing (stock still not added) or delete */}
      <Dialog open={draftsOpen} onOpenChange={setDraftsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FolderOpen className="h-5 w-5 text-primary" /> Saved drafts</DialogTitle>
            <DialogDescription>These are saved but <b>not</b> added to stock. Resume one to edit and finalise.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] space-y-2 overflow-auto" data-testid="bulkadd-drafts-list">
            {drafts.length === 0 && (
              <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No drafts yet. Use "Save &amp; edit later" to keep your progress.
              </div>
            )}
            {drafts.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`bulkadd-draft-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{d.title}</p>
                  <p className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{d.item_count} item{d.item_count === 1 ? '' : 's'} · {d.piece_count} pc</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{d.updated_at ? new Date(d.updated_at).toLocaleString() : ''}</span>
                  </p>
                  {d.created_by && <p className="text-[11px] text-muted-foreground">by {d.created_by}</p>}
                </div>
                <Button size="sm" className="h-8 rounded-lg" onClick={() => resumeDraft(d.id)} data-testid={`bulkadd-draft-resume-${d.id}`}>Resume</Button>
                <button onClick={() => deleteDraft(d.id)} data-testid={`bulkadd-draft-delete-${d.id}`}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="secondary" className="rounded-lg" onClick={() => setDraftsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
